import React from 'react';

import { LngLat } from '@foreflight/webmap/core';
import {
    AeroLayer,
    FFBasemapLayer,
    FFChartLayer,
    FFMap,
    FFRasterMapLayer,
    FFTrackLayer,
    PlaceLabelsLayer,
} from '@foreflight/webmap/react';

import {
    useChrono,
    useCurrentTracklog,
    useMapControls,
    useMapSettings,
} from 'ff/debrief/hooks';
import MapContent from 'ff/debrief/layouts/MapContent';
import MapAreaGrid from 'ff/debrief/layouts/MapAreaGrid';

import OwnshipMapLayer from 'ff/debrief/components/map/OwnshipMapLayer';
import { isFeatureEnabled } from 'ff/common/ffAppUtils';
import { AirportSettings } from 'ff/debrief/models';

interface Props {}

const Map = (props: React.PropsWithChildren<Props>) => {
    const { coordinates, viewport } = useCurrentTracklog();
    const { currentPoint } = useChrono();
    const { currentSettings } = useMapSettings();

    return (
        <MapContent>
            {/* Only known way to set the height correctly. 50px accounts for header. */}
            <FFMap
                height="calc(100vh - 50px)"
                theme={currentSettings?.theme}
                viewport={viewport}
                boundsPadding={{ top: 15, right: 0, bottom: 210, left: 310 }}
                resourcesUrl="{apiRoot}/map/api/resources"
            >
                <MapLayers coordinates={coordinates} />
                {currentPoint && isFeatureEnabled('debrief_features') && (
                    <OwnshipMapLayer
                        latitude={currentPoint?.latitude}
                        longitude={currentPoint?.longitude}
                        bearing={currentPoint?.bearing}
                    />
                )}
                <MapAreaGrid>{props.children}</MapAreaGrid>
            </FFMap>
        </MapContent>
    );
};

const MapLayers: React.FC<{ coordinates: LngLat[] }> = ({ coordinates }) => {
    const { currentLayer, aeroLayer } = useMapControls();
    const { currentSettings, allAeroLayersDisabled } = useMapSettings();
    const culturalElements = currentSettings?.culturalElements;
    let airportSettings: AirportSettings | boolean = false;
    if (
        currentSettings?.airports &&
        Boolean(currentSettings?.filters?.airports)
    ) {
        if (currentSettings?.filters?.airports) {
            airportSettings = currentSettings?.filters?.airports;
        } else {
            airportSettings = false;
        }
    }
    const aeronavFilters = { ...currentSettings?.filters };

    return (
        <>
            <FFBasemapLayer
                major_roads={culturalElements?.major_roads}
                minor_roads={culturalElements?.minor_roads}
                railroads={culturalElements?.railroads}
                urban_areas={culturalElements?.urban_areas}
                forests_parks={culturalElements?.forests_parks}
                peaks_passes={culturalElements?.peaks_passes}
                points_of_interest={culturalElements?.points_of_interest}
                power_lines={culturalElements?.power_lines}
            />
            {currentSettings?.placeLabels && <PlaceLabelsLayer />}
            {/* Hacky method to remove Aero Layer from Dom when last setting changes to false*/}
            {!allAeroLayersDisabled() && aeroLayer && (
                <AeroLayer
                    filters={{
                        airspace: currentSettings.airspace && {
                            ...currentSettings?.airspaceFilters,
                        },
                        artcc: false,
                        artccfir: false,
                        artccfisa: false,
                        atcsec: false,
                        styleOptions: { airwayLabelMinzoom: true },
                        airports: currentSettings?.airports && airportSettings,
                        navlevel: aeronavFilters?.navlevel,
                        airways: aeronavFilters?.airways,
                        navaids: aeronavFilters?.navaids,
                        waypoints: aeronavFilters?.waypoints,
                        euData: false,
                    }}
                />
            )}

            {(currentLayer === 'street' || currentLayer === 'aerial') && (
                <FFRasterMapLayer name={currentLayer} />
            )}
            {currentLayer &&
                currentLayer !== 'street' &&
                currentLayer !== 'aerial' && (
                    <FFChartLayer name={currentLayer} />
                )}
            {coordinates && <FFTrackLayer points={coordinates} />}
        </>
    );
};

export default Map;
